import { Loader } from "@googlemaps/js-api-loader";


const loader = new Loader( {
    apiKey: Nu.maps.api_key,
    version: "beta",
} );

const pinImg = document.createElement( 'img' );
pinImg.src = '/wp-content/themes/nu-inclu/assets/images/map-pin.svg';

initMap();

document.addEventListener( 'htmx:load', initMap );

async function initMap() {
    const panelTemplate = getPanelTemplate();
    const mapWrapper = document.getElementById( 'archive-tour-map' );
    const mapElement = document.getElementById( 'google-map' );

    if ( !mapWrapper || !mapElement || !panelTemplate ) return;

    const [
        { Marker, AdvancedMarkerElement, AdvancedMarkerClickEvent },
        { Map, OverlayView }
    ] = await Promise.all( [
        loader.importLibrary( 'marker' ),
        loader.importLibrary( "maps" )
    ] );

    const map = new Map( mapElement, {
        zoom: Number( mapElement?.getAttribute( 'zoom' ) ),
        center: {
            lat: Number( mapElement?.getAttribute( 'center-lat' ) ),
            lng: Number( mapElement?.getAttribute( 'center-lng' ) ),
        },
        mapId: mapElement?.getAttribute( 'map-id' ),
        fullscreenControl: false,
        scrollWheelControl: false,
        streetViewControl: false,
    } );

    const markers = Nu.maps.tours.map( tour => {
        if ( !tour.latitude || !tour.longitude ) return;

        const marker = new AdvancedMarkerElement( {
            title: tour.title,
            map,
            position: {
                lat: Number( tour.latitude ),
                lng: Number( tour.longitude )
            },
            content: pinImg.cloneNode(),
            gmpClickable: true,
        } );

        marker.setAttribute( 'data-tour-id', tour.post_id );

        return marker;
    } ).filter( ( item ) => item );



    class TourOverlay extends OverlayView {
        /**
         * @type {null|number}
         */
        id;

        static breakpoint = window.matchMedia( '(min-width: 640px)' );

        constructor() {
            super();
            this.id = null;
            this.panel = null;  // Reference to the div containing the overlay
        }

        onAdd() {
            const isNew = this.panel?.id !== this.id?.toString();

            if ( isNew ) {
                this.panel?.remove();
                this.panel = getTourHTML( this.id );
                mapWrapper.appendChild( this.panel );
            }
            this.panel.style.position = 'absolute';
            this.panel.style.transformOrigin = '0 0';

            if ( TourOverlay.breakpoint.matches ) {
                this.panel.style.setProperty( 'inset', '0 -100rem auto 0' );
            } else {
                this.panel.style.setProperty( 'inset', '5%' );
            }

            if ( isNew ) {
                this.panel.animate( {
                    transform: ['scale(0.8)', 'scale(1)'],
                }, {
                    duration: 400,
                    easing: 'cubic-bezier(.4,1.21,.72,1.05)',
                } );
                this.panel.animate( {
                    opacity: ['0', '1'],
                }, {
                    duration: 400,
                    easing: 'ease',
                } );
            }
        }

        // Called when the map is panned or zoomed, to update the position of the overlay
        draw() {
            if ( !this.panel ) return;

            if ( TourOverlay.breakpoint.matches ) {
                this.panel.style.setProperty( 'inset', '0 -100rem auto 0' );

                const projection = this.getProjection();
                const position = projection.fromLatLngToContainerPixel( this.position );

                if ( position ) {
                    this.panel.style.setProperty( 'translate', `${position.x}px ${position.y}px` );
                }
            } else {
                this.panel.style.setProperty( 'inset', '5%' );
                this.panel.style.setProperty( 'translate', `0px 0px` );
            }
        }

        // Set position of the overlay
        setPosition( latLng ) {
            this.position = latLng;
            this.draw();
        }

        // Called when the overlay is removed
        onRemove() {
            if ( this.panel ) {
                this.panel.remove();
            }
            this.panel = null;
        }
    }

    const overlay = new TourOverlay();

    markers.forEach( marker => {

        marker.addEventListener( 'gmp-click', ( e ) => {
            /** REQUIRED to prevent maps scrolling the window all over the place */
            let scroll = window.scrollY;
            requestAnimationFrame( () => {
                window.scrollTo( { top: scroll } );
            } );
            overlay.id = e.srcElement.getAttribute( 'data-tour-id' );
            overlay.setMap( map );
            overlay.setPosition( marker.position );
        } );
    } );

}


/**
 * @return {HTMLTemplateElement|null}
 */
function getPanelTemplate() {
    return document.getElementById( 'tour-map-panel' );
}

/**
 * @typedef {{
 *  post_id: number,
 *  title: string,
 *  permalink: string,
 *  thumbnail: string,
 *  thumbnail_img: string,
 *  types: string[]
 *  location: string,
 *  latitude: string,
 *  longitude: string,
 *  is_new: boolean,
 * }} Tour
 * }
 */

/**
 * @param {number} id
 *
 * @return {HTMLElement|null}
 */
function getTourHTML( id ) {
    const tour = /** @type {Tour} */ ( Nu.maps.tours.find( ( tour ) => tour.post_id == id ) );
    if ( !tour ) return null;

    const panel = getPanelTemplate()?.content?.cloneNode( true );

    const panelContent = panel.firstElementChild;
    panelContent.setAttribute( 'id', id.toString() );
    const flash = panelContent.querySelector( '.card-flash' );
    flash.classList.toggle( 'hidden', !tour.is_new );
    const titleSlot = panelContent.querySelector( '[data-title]' );
    const linkSlot = panelContent.querySelector( '[data-permalink]' );
    const imageSlot = panelContent.querySelector( '[data-thumbnail]' );
    const typesSlot = panelContent.querySelector( '[data-type]' );
    const locationSlot = panelContent.querySelector( '[data-location]' );
    const closer = panelContent.querySelector( '[data-close]' );

    titleSlot.textContent = tour.title;
    linkSlot.href = tour.permalink;
    imageSlot.innerHTML = tour.thumbnail_img;
    typesSlot.textContent = tour.terms['travel-type'].join( ', ' );
    locationSlot.textContent = tour.location;

    closer.addEventListener( 'click', ( _e ) => {
        panelContent.remove();
    } );

    return panelContent;
}
